import { useCallback, useEffect, useState } from 'react';
import type { AccountNode } from '../api/types.js';

/**
 * The chart's account scope, and the header search's pins (FR-075, FR-076).
 *
 * Two pieces of state that look unrelated and are not: the account list is the only thing that
 * sets the scope, and a pin is how a reader keeps the account they set it from within reach.
 */

/** What the wire calls each level. `parent` is `client` on the request — see `SCOPE_TYPE_OF`. */
export type WireScopeType = 'client' | 'msp' | 'child';

/**
 * The node type → request scope map.
 *
 * ⚠ **The one place the two vocabularies meet, and it exists rather than being inlined because
 * the mismatch is deliberate.** The request has said `client` since before the other two levels
 * existed — every `?client=` link a reader has bookmarked, every ⌘K `href` the server has
 * emitted, and the prefetch keys all carry it (FR-050 makes those linkable on purpose). The
 * *table* says `Parent client`, because that is what distinguishes the row from the child below
 * it. Renaming either side to match the other breaks links or breaks the column.
 */
export const SCOPE_TYPE_OF: Record<AccountNode['type'], WireScopeType> = {
  msp: 'msp',
  parent: 'client',
  child: 'child',
};

/** How each level reads in the Type column, and in a chart title. */
export const NODE_TYPE_LABEL: Record<AccountNode['type'], string> = {
  msp: 'MSP',
  parent: 'Parent client',
  child: 'Child client',
};

export interface AccountScope {
  scopeType: WireScopeType;
  scopeKey: string;
}

/**
 * Reads the scope out of the URL.
 *
 * ## The two parameters, and why there are two
 *
 * `?client=K` is the **breakdown highlight** and, when nothing else is set, the chart scope too
 * — for a parent client those are the same fact, which is why one parameter has always carried
 * both. `?scopeType=`/`?scopeKey=` is the chart scope when it is *not* a parent client, and it
 * sits beside `?client=` rather than replacing it: a child client scopes the chart to a facility
 * while highlighting its **parent's** breakdown row, and those are two different keys.
 *
 * ⚠ **`?client=` is still read as a scope, and must stay read.** It is the one-parameter
 * spelling that predates the other two levels and is what ⌘K results and shared links carry
 * (FR-050 makes those linkable on purpose); dropping it turns every such link into an unscoped
 * region chart, silently — the reader lands on the page they bookmarked and the client they
 * bookmarked it *for* is simply not selected.
 *
 * ⚠ **The pair wins over `?client=`**, so an address carrying both resolves to the newer
 * spelling rather than to whichever the URL happened to list first. That precedence is what
 * makes a child scope expressible at all.
 */
export function scopeFromParams(params: URLSearchParams): AccountScope | null {
  const type = params.get('scopeType');
  const key = params.get('scopeKey');
  if (type && key && isWireScopeType(type)) return { scopeType: type, scopeKey: key };

  const legacy = params.get('client');
  if (legacy) return { scopeType: 'client', scopeKey: legacy };

  return null;
}

/**
 * The by-client breakdown row to highlight, or `null`.
 *
 * Always `?client=`, at every scope level — which is what lets the breakdown keep matching on
 * one key it has always matched on, while the chart scope moves through three levels beside it.
 * `null` on an MSP scope, because an MSP spans many parents and there is no single row it means.
 */
export function highlightFromParams(params: URLSearchParams): string | null {
  return params.get('client');
}

/**
 * Writes a scope and its highlight into the URL, or clears both.
 *
 * ⚠ **Both are always cleared first.** A leftover `?client=` beside a new `?scopeType=msp` is a
 * highlighted row from the previous selection sitting under a chart of something else — two
 * true answers making one false screen, which is the same failure the snapshot guard in
 * `dataStore` exists to prevent, at a smaller scale.
 */
export function scopeToParams(
  params: URLSearchParams,
  selection: { scope: AccountScope | null; highlightClientKey?: string | null },
): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('client');
  next.delete('scopeType');
  next.delete('scopeKey');

  const { scope } = selection;
  if (!scope) return next;

  if (scope.scopeType === 'client') {
    // A parent client keeps the short, linkable spelling, so an address shared from this page is
    // the same address ⌘K would have produced for it — and it is its own highlight.
    next.set('client', scope.scopeKey);
    return next;
  }

  next.set('scopeType', scope.scopeType);
  next.set('scopeKey', scope.scopeKey);
  // A child carries its parent so the breakdown can highlight it. An MSP passes `null` and
  // therefore writes nothing, which is the absence the breakdown reads as "no row".
  if (selection.highlightClientKey) next.set('client', selection.highlightClientKey);

  return next;
}

function isWireScopeType(value: string): value is WireScopeType {
  return value === 'client' || value === 'msp' || value === 'child';
}

/** The scope a node selects. */
export function scopeForNode(node: AccountNode): AccountScope {
  return { scopeType: SCOPE_TYPE_OF[node.type], scopeKey: node.key };
}

/** Whether a node is the one currently scoping the chart. */
export function isNodeSelected(node: AccountNode, scope: AccountScope | null): boolean {
  if (!scope) return false;
  return scope.scopeType === SCOPE_TYPE_OF[node.type] && scope.scopeKey === node.key;
}

// ---------------------------------------------------------------------------
// Pins
// ---------------------------------------------------------------------------

/**
 * Pinned accounts, per region, in `localStorage`.
 *
 * ⚠ **This is the one piece of view state that deliberately does NOT use `state/persist.ts`,
 * and the departure is the point.** That helper is `sessionStorage` because a stale *filter*
 * puts a months-old combination in front of someone who believes they are on the defaults —
 * which is how a person misreads a number. A pin changes no figure and selects no filter: it
 * reorders rows in a search list. Its whole purpose is "the four accounts I open every day", and
 * a memory that dies with the tab does not serve that at all.
 *
 * **Namespaced by signed-in address**, for the reason `dataStore.ts` gives: `localStorage`
 * outlives a session on a shared machine, and one person's book of accounts must not greet the
 * next. Unlike the data store this holds no figures, so it is not *wiped* on sign-out — it is
 * simply never read under another address.
 *
 * Every access is wrapped: Safari's private mode throws on `localStorage` rather than returning
 * null, and a search panel that cannot remember a pin must still open.
 */
const PIN_PREFIX = 'bob:pins:';

function pinKey(owner: string, regionCode: string): string {
  return `${PIN_PREFIX}${owner.toLowerCase()}|${regionCode}`;
}

/** `msp:amn` — the level is part of the identity, because keys are not unique across levels. */
export function pinIdFor(node: AccountNode): string {
  return `${node.type}:${node.key}`;
}

export function readPins(owner: string, regionCode: string): string[] {
  try {
    const raw = window.localStorage.getItem(pinKey(owner, regionCode));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    // Untrusted input: written by an older deploy, or by hand. A malformed entry is treated as
    // no pins rather than as an error, exactly as `loadPersisted` does.
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    return [];
  }
}

function writePins(owner: string, regionCode: string, pins: string[]): void {
  try {
    window.localStorage.setItem(pinKey(owner, regionCode), JSON.stringify(pins));
  } catch {
    // Quota, or a locked-down browser. Losing the memory of a pin is not worth an error state.
  }
}

export interface PinStore {
  pins: string[];
  isPinned: (node: AccountNode) => boolean;
  toggle: (node: AccountNode) => void;
}

/**
 * The pin list for one region, with a live memory.
 *
 * Pins are held in **insertion order and rendered in it**, so the most recently pinned account
 * lands at the bottom of the pinned block rather than jumping to the top. Re-sorting the block
 * on every pin would move the rows a reader is aiming at.
 */
export function usePins(owner: string, regionCode: string): PinStore {
  const [pins, setPins] = useState<string[]>(() => readPins(owner, regionCode));

  // Re-read on a region or owner change: this hook outlives both, and keeping the previous
  // region's pins would show one region's accounts pinned in another's list.
  useEffect(() => {
    setPins(readPins(owner, regionCode));
  }, [owner, regionCode]);

  const toggle = useCallback(
    (node: AccountNode) => {
      const id = pinIdFor(node);
      setPins((current) => {
        const next = current.includes(id)
          ? current.filter((p) => p !== id)
          : [...current, id];
        writePins(owner, regionCode, next);
        return next;
      });
    },
    [owner, regionCode],
  );

  const isPinned = useCallback(
    (node: AccountNode) => pins.includes(pinIdFor(node)),
    [pins],
  );

  return { pins, isPinned, toggle };
}

// ---------------------------------------------------------------------------
// Filtering and ordering
// ---------------------------------------------------------------------------

/**
 * The list as the panel renders it: pinned rows first, then the rest in server order.
 *
 * ⚠ **The server order is MSPs → parents → children and is not re-sorted here.** It is a stored
 * `typeRank` sort (`account_hierarchy`), and re-deriving it in the browser would put the rule in
 * two places — the one that decides what is *shown* and the one that decides what it *means*.
 *
 * The query is matched against the node's own name and its parent's, so typing a health
 * system's name surfaces its facilities and typing an MSP's surfaces its clients. Matching is a
 * substring test rather than a prefix one, because a facility is usually named
 * `"<system> - <city>"` and a prefix match on it can only ever find the system.
 */
export function filterAndOrder(
  nodes: AccountNode[],
  query: string,
  pins: string[],
): AccountNode[] {
  const q = query.trim().toLowerCase();

  const matched = q
    ? nodes.filter(
        (node) =>
          node.name.toLowerCase().includes(q) ||
          (node.parentName?.toLowerCase().includes(q) ?? false) ||
          (node.mspName?.toLowerCase().includes(q) ?? false),
      )
    : nodes;

  if (pins.length === 0) return matched;

  const rank = new Map(pins.map((id, index) => [id, index]));
  const pinned: AccountNode[] = [];
  const rest: AccountNode[] = [];

  for (const node of matched) {
    if (rank.has(pinIdFor(node))) pinned.push(node);
    else rest.push(node);
  }

  pinned.sort((a, b) => rank.get(pinIdFor(a))! - rank.get(pinIdFor(b))!);

  return [...pinned, ...rest];
}
